import { BaseUserWithRole, BaseCompany } from '@/models/User';

// ------------------- All interfaces for responding to API requests -------------------------------
export enum InvoiceStatus {
  Draft = 'DRAFT',
  Unpaid = 'UNPAID', // Comes from the backend and is converted to the status Due, Open, or Sent.
  Paid = 'PAID',
  Cancelled = 'CANCELLED',
  CancellationCreated = 'CANCELLATION_CREATED',
  Due = 'DUE', // These are statuses that are created in the frontend from the UNPAID status. They do not come from the backend.
  Open = 'OPEN', // These are statuses that are created in the frontend from the UNPAID status. They do not come from the backend.
  Sent = 'SENT', // These are statuses that are created in the frontend from the UNPAID status. They do not come from the backend.
}

export enum InvoiceDueStatus {
  None = 'NONE',
  Due = 'DUE',
  Overdue = 'OVERDUE',
  Hold = 'HOLD',
}
export type InvoiceType = 'INVOICE' | 'CANCEL' | 'CORRECTION';
export type InvoiceUnitType = 'HOURS' | 'DAYS' | 'PIECE' | 'FIXED' | 'OTHER';
export type InvoiceUserType = 'sender' | 'receiver';
export type SalutationType = 'NONE' | 'MR' | 'MS';
export type InvoiceViolationsReasonType = 'MANDATORY_CHECK' | 'DUPLICATE_CHECK';
export type invoicePaidMarkedByType = 'SELLER' | 'BUYER';

export interface ContractReference {
  contractId: string;
  contractBytecookieId: string;
}

export interface Address {
  street: string;
  houseNumber: string;
  supplement: string;
  postalCode: string;
  city: string;
  countryIsoCode: string;
  phoneNumber: string;
}

export interface InvoiceBaseCompany
  extends Omit<BaseCompany, 'rootUserId'> {
  rootUserId: string;
}

export interface InvoiceBaseUserWithRole
  extends Omit<BaseUserWithRole, 'userId' | 'company'> {
  userId?: string;
  company?: InvoiceBaseCompany;
}

export interface InvoicePartner {
  user: InvoiceBaseUserWithRole;
  company: InvoiceBaseCompany;
  address: Address;
  registerNumber: string;
  registerEntity: string;
}

export interface BankAccount {
  id: string;
  iban: string;
  bic: string;
  accountName: string;
  currency: string;
  taxId: string;
  taxRate: number;
  reverseCharge: boolean;
  reverseChargeTaxId: string;
}

export interface InvoiceAttachment {
  id: string;
  position: number;
  created: string;
  filename: string;
  bInvoice: boolean;
  size: number;
  type: string;
}

export interface InvoiceItem {
  id: string;
  posNo: number;
  unit: InvoiceUnitType;
  description: string;
  amount: number;
  costSingle: number;
  unitDescription: string;
}

export interface ExtendedInvoiceItem extends InvoiceItem {
  netSum: string;
  displayedUnit: string;
}

export interface Invoice {
  invoiceId: string;
  bcReferenceId: string;
  created: string;
  issueDate: string;
  dueDate: string;
  performancePeriodFrom: string;
  performancePeriodTo: string;
  status: InvoiceStatus;
  invoiceType: InvoiceType;
  invoiceDocId: string;
  account: BankAccount;
  receiver: InvoicePartner;
  sender: InvoicePartner;
  contractReference: ContractReference;
  items: InvoiceItem[];
  attachments: InvoiceAttachment[];
  title: string;
  intro: string;
  footer: string;
  reference: string;
  costCenter: string;
  invoiceCancelDocId: string;
  invoiceCorrectionDocId: string;
  mandatoryFieldPaths: string[];
  dueStatus: InvoiceDueStatus;
  expansionDays: number | undefined;
  readDate: string;
  lastUpdated: string;
  changedDate: string;
  sentDate: string;
  paidDate: string;
  invoicePaidMarkedBy: invoicePaidMarkedByType;
}

export interface InvoiceViolation {
  fieldPath: string;
  rule?: string;
  pattern: string;
  description: string;
  reason: InvoiceViolationsReasonType;
}

// ------------------- Interfaces used several times -------------------------------

export interface InvoiceAddressData {
  street: string;
  houseNumber: string;
  supplement: string;
  countryIsoCode: string;
  postalCode: string;
  city: string;
  company: string;
  salutation: SalutationType | string;
  firstname: string;
  lastname: string;
  phoneNumber: string;
  registerNumber: string;
  registerEntity: string;
}

export interface AttachmentFile {
  attachment: string;
  fileType: string;
  filename: string;
}

// ------------------- All interfaces for the payloads for API requests -------------------------------

export interface PayloadCreateInvoice {
  contractId?: string;
  contractPartnerId?: string;
  contractPartnerCompanyId?: string;
  refInvoiceId?: string;
  invoiceType: InvoiceType;
}

export interface PayloadBaseData {
  invoiceDocId: string;
  performancePeriodFrom: string;
  performancePeriodTo: string;
  issueDate: string;
  dueDate: string;
  contractId: string;
  reference: string;
}

export interface PayloadAccountData {
  iban: string;
  bic: string;
  accountName: string;
  currency: string;
  taxId: string;
  taxRate: number;
  reverseCharge: boolean;
  reverseChargeTaxId: string;
}

export interface PayloadInvoiceText{
  intro: string;
  footer: string;
}

export interface PayloadUpdateInvoice {
  sender?: InvoiceAddressData;
  receiver?: InvoiceAddressData;
  baseData?: PayloadBaseData;
  accountData?: PayloadAccountData;
  invoiceText?: PayloadInvoiceText;
}

export interface PayloadExtendInvoice {
  expansionDays: number;
}

export interface PayloadCancel {
  cancelType: InvoiceType;
  invoiceDocId: string;
}

export interface PayloadAttachment {
  description: string;
  file: AttachmentFile;
}

export interface PayloadUpdateReadDateInvoice {
  readDate: string;
}

// ------------------- All interfaces for the objects used in components  -------------------------------

export interface ComponentInvoiceUser {
  firstname?: string;
  lastname?: string;
  userId?: string;
}

export interface ComponentAddressData extends InvoiceAddressData{
  zipAndCity: string;
}

export interface ComponentInvoice {
  invoiceId: string;
  user: InvoiceBaseUserWithRole;
  reference: string;
  costCenter: string;
  dueDate: string;
  status: InvoiceStatus;
  bcReferenceId: string;
  issueDate: string;
  invoiceDocId: string;
  totalAmount: number;
  contractBytecookieId: string;
  contractId: string;
  created: string;
  currency: string;
  dueStatus: InvoiceDueStatus;
  readDate: string;
  lastUpdated: string;
  isUserSender: boolean;
  invoicePaidMarkedBy: invoicePaidMarkedByType;
  changedDate: string;
  sentDate: string;
  expansionDays: number | undefined;
  invoiceCancelDocId: string;
}

export interface ComponentInvoicePaymentData {
  accountName: string;
  iban: string;
  bic: string;
  currency: string;
  taxId: string;
  taxRate: number;
  reverseCharge: boolean;
  reverseChargeTaxId: string;
}

export interface ComponentInvoiceBasisData {
  invoiceDocId: string;
  invoiceType: InvoiceType;
  issueDate: string;
  performancePeriodFrom: string;
  performancePeriodTo: string;
  dueDate: string;
  associatedContract: string;
  reference: string;
  invoiceCorrectionDocId: string;
}

export interface ComponentInvoiceTextData {
  intro: string;
  footer: string;
}

// ------------------- Other -------------------------------

export interface ValidateInputs {
  validateInputs(): Promise<boolean>;
}

export interface StorableComponentsStatus {
  isUnsaved: boolean;
  isProcessing: boolean | null;
}

export interface StorableComponents {
  addressData: StorableComponentsStatus;
  paymentData: StorableComponentsStatus;
  positionsData: StorableComponentsStatus;
}

export type TypeOfPopUp =
  | 'extend-due-date'
  | 'correction-or-cancellation-invoice'
  | 'send-invoice'
  | 'paid-invoice'
  | 'delete-invoice'
  | 'correction-invoice'
  | 'cancellation-invoice'
  | 'invoice-missing-fields'
  | 'check-successfully'
  | 'correction-confirmation-invoice'
  | 'cancellation-confirmation-invoice'
  | 'invoice-is-creating'
  | 'invoice-has-been-sent'
  | 'unsaved-data'
  | 'unsaved-data-failed'
  | 'check-invoice'
  | 'correction-is-creating'
  | 'cancellation-is-creating';

export interface InvoiceEventPayload {
  invoice: Invoice;
  invoiceViolation?: InvoiceViolation[];
}
